<template>
    <admin-page :result="result" description="Vincula dos cuentas existentes sin pasar por la invitación">
        <segmented-tabs class="mode-tabs" :options="modeOptions" :value="mode" @change="onChangeMode" />

        <template v-if="isCompanyMode">
            <div class="form-grid">
                <kimsa-text-field @change="companyEmail = $event" :value="companyEmail" label="Email de la empresa"
                    placeholder="empresa@correo.com" dense hide-details />
                <kimsa-text-field @change="coachEmail = $event" :value="coachEmail" label="Email del entrenador"
                    placeholder="entrenador@correo.com" dense hide-details />
            </div>
        </template>

        <template v-else>
            <div class="form-grid">
                <kimsa-text-field @change="coachEmail = $event" :value="coachEmail" label="Email del entrenador"
                    placeholder="entrenador@correo.com" dense hide-details />
                <kimsa-text-field @change="studentEmail = $event" :value="studentEmail" label="Email del alumno"
                    placeholder="alumno@correo.com" dense hide-details />
            </div>
        </template>

        <v-btn color="primary" class="black--text attach-btn mt-5" depressed :loading="loading" :disabled="disabled"
            @click="showConfirm">
            Enlazar
        </v-btn>

        <kimsa-confirm ref="confirm" show-cancel title="Enlazar usuarios" :content="confirmContent"
            :persistent="false" :close-on-confirm="false" :loading="loading" @confirm="attachUser" />
    </admin-page>
</template>

<script>
import KimsaTextField from '@/components/common/inputs/KimsaTextField.vue'
import KimsaConfirm from '@/components/common/KimsaConfirm.vue'
import SegmentedTabs from '@/components/common/SegmentedTabs.vue'
import AdminPage from '@/components/admin/AdminPage.vue'
import { db, Timestamp, FieldValue } from '@/utils/Firebase'
import { adminRouteGuard } from '@/utils/Admin'

export default {
    name: 'AdminAttachUsers',
    components: { KimsaTextField, KimsaConfirm, SegmentedTabs, AdminPage },
    beforeRouteEnter: adminRouteGuard,
    data() {
        return {
            mode: 'company',
            modeOptions: [
                { name: 'Entrenador a empresa', value: 'company' },
                { name: 'Alumno a entrenador', value: 'coach' },
            ],
            companyEmail: '',
            coachEmail: '',
            studentEmail: '',
            loading: false,
            result: null,
        }
    },
    computed: {
        isCompanyMode(vm = this) {
            return vm.mode === 'company'
        },
        disabled(vm = this) {
            if (!vm.coachEmail) return true
            return vm.isCompanyMode ? !vm.companyEmail : !vm.studentEmail
        },
        confirmContent(vm = this) {
            if (vm.isCompanyMode)
                return `Vas a enlazar al entrenador <b>${vm.coachEmail.trim()}</b> con la empresa <b>${vm.companyEmail.trim()}</b>. El entrenador pasa a ser coach_company.`
            return `Vas a enlazar al alumno <b>${vm.studentEmail.trim()}</b> con el entrenador <b>${vm.coachEmail.trim()}</b>. Se borra su suscripción anterior si tenía otra.`
        },
    },
    mounted() {
        const vm = this
        const { email, type } = vm.$route.query
        if (!email) return

        if (type === 'student') {
            vm.mode = 'coach'
            vm.studentEmail = email
            return
        }
        if (type === 'company') {
            vm.mode = 'company'
            vm.companyEmail = email
            return
        }
        vm.mode = 'company'
        vm.coachEmail = email
    },
    methods: {
        showConfirm() {
            if (this.disabled) return
            this.$refs.confirm.show()
        },
        onChangeMode(mode) {
            const vm = this
            vm.mode = mode
            vm.result = null
        },
        fail(message) {
            this.result = { type: 'error', message }
            return null
        },
        async findUser(email, types, label) {
            const vm = this
            const snapshot = await db.collection('users').where('email', '==', email.trim()).get()
            if (snapshot.docs.length === 0) return vm.fail(`No se encontró ${label} con ese email.`)
            const user = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() }
            if (!types.includes(user.accountType)) return vm.fail(`La cuenta ${email} no es ${label}.`)
            return user
        },
        async attachUser() {
            const vm = this
            vm.result = null
            vm.$refs.confirm.hide()
            await vm.withLoading(async () => {
                const coach = await vm.findUser(vm.coachEmail, ['coach', 'coach_company'], 'un entrenador')
                if (!coach) return

                if (vm.isCompanyMode) {
                    const company = await vm.findUser(vm.companyEmail, ['company'], 'una empresa')
                    if (!company) return

                    const subscription = await db.collection('subscriptions').add({
                        coachId: coach.id,
                        companyId: company.id,
                        createdAt: Timestamp.now(),
                        paused: false,
                    })

                    await db.collection('users').doc(coach.id).update({
                        accountType: 'coach_company',
                        coachSubscription: {
                            companyDescription: company?.description || '',
                            companyId: company.id,
                            companyName: company?.name || '',
                            id: subscription.id,
                            paused: false,
                            attachMode: 'thrid_party',
                        },
                    })

                    await db.collection('users').doc(company.id).update({
                        activeCoaches: FieldValue.increment(1),
                    })

                    vm.result = { type: 'success', message: `${coach.email} quedó enlazado a ${company.email}.` }
                    vm.companyEmail = ''
                    vm.coachEmail = ''
                    return
                }

                const student = await vm.findUser(vm.studentEmail, ['student'], 'un alumno')
                if (!student) return

                const existingSubs = await db.collection('subscriptions').where('studentId', '==', student.id).get()
                for (const doc of existingSubs.docs) await doc.ref.delete()

                const subscription = await db.collection('subscriptions').add({
                    coachId: coach.id,
                    studentId: student.id,
                    createdAt: Timestamp.now(),
                    paused: false,
                })

                await db.collection('users').doc(student.id).update({
                    studentSubscription: {
                        coachDescription: coach?.description || '',
                        coachId: coach.id,
                        coachName: coach?.name || '',
                        id: subscription.id,
                        paused: false,
                        attachMode: 'thrid_party',
                    },
                })

                vm.result = { type: 'success', message: `${student.email} quedó enlazado a ${coach.email}.` }
                vm.studentEmail = ''
                vm.coachEmail = ''
            })
        },
    },
}
</script>

<style scoped>
.mode-tabs {
    max-width: 380px;
    margin-bottom: 20px;
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.attach-btn {
    height: 40px !important;
    min-width: 160px;
    border-radius: 8px;
}

@media (max-width: 600px) {
    .form-grid {
        grid-template-columns: 1fr;
    }

    .mode-tabs {
        max-width: none;
    }

    .attach-btn {
        width: 100%;
    }
}
</style>
